import { logEvent } from '../analytics/firAnalytics'
import {
  PLL_VIDEO_ACCOUNT_ID,
  DEFAULT_VIDEO_PLAYER_ID,
  VIDEO_PLAYER_SCRIPT,
} from '../../Config/Video/config'
import { FULL_VIDEO_VIEW_HEIGHT } from '../../Constants/video'
import { ANALYTICS_TAGS } from '../../Constants/analytics'

export const sendVideoStart = (params: any) => {
  logEvent(ANALYTICS_TAGS.video_start, params)
}

export const sendVideoComplete = (params: any) => {
  logEvent(ANALYTICS_TAGS.video_watched, {
    percentage_watched: '1',
    ...params,
  })
}

export const sendVideoPause = (params: any) => {
  logEvent(ANALYTICS_TAGS.video_watched, params)
}

export const videoLog = (name: string, params: any) => {
  logEvent(name, params)
}

export const calcDimensionsFromAspectRatio = (
  aspectRatio: string,
  height: number,
  isMobile: boolean
) => {
  let updatedHeight = height
  switch (aspectRatio) {
    case '16:9':
      updatedHeight = isMobile ? height - 60 : height
      return (updatedHeight / 9) * 16
    case '4:3':
      updatedHeight = isMobile ? height - 40 : height
      return (updatedHeight / 3) * 4
    case '1:1':
      updatedHeight = isMobile ? height - 25 : height
      return updatedHeight
    case '3:4':
      return (height / 4) * 3
    case '9:16':
      return (height / 16) * 9
    case '4:5':
      return (height / 5) * 4
    default:
      return (height / 9) * 16
  }
}

let scriptLoadingPromise: Promise<void> | null = null
let scriptLoaded = false

export const loadBrightcoveScript = (): Promise<void> => {
  if (scriptLoaded) return Promise.resolve()
  if (scriptLoadingPromise) return scriptLoadingPromise

  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    if (typeof window !== 'undefined' && (window.bc || window.videojs)) {
      scriptLoaded = true
      resolve()
      return
    }

    const s = document.createElement('script')
    s.src = VIDEO_PLAYER_SCRIPT
    s.async = true
    s.onload = () => {
      scriptLoaded = true
      resolve()
    }
    s.onerror = () => {
      scriptLoadingPromise = null
      reject(new Error('Failed to load Brightcove script'))
    }
    document.head.appendChild(s)
  })

  return scriptLoadingPromise
}

export const createVideoElement = (
  id: string,
  videoId: string,
  videoWidth: string
): HTMLElement => {
  const el = document.createElement('video-js') as HTMLElement
  el.id = id
  el.className = 'video-js vjs-big-play-centered'
  el.setAttribute('data-account', PLL_VIDEO_ACCOUNT_ID || '')
  el.setAttribute('data-player', DEFAULT_VIDEO_PLAYER_ID)
  el.setAttribute('data-embed', 'default')
  el.setAttribute('playsinline', '')
  el.setAttribute('controls', '')
  el.setAttribute('data-video-id', videoId)
  el.style.width = videoWidth
  el.style.height = `${FULL_VIDEO_VIEW_HEIGHT}vh`
  el.style.display = 'block'

  return el
}

export const getPlayerInstance = (id: string, element: HTMLElement): unknown => {
  return window.videojs?.getPlayer?.(id) || window.bc?.(element)
}

export const waitForPlayerReady = (player: any): Promise<void> => {
  return new Promise<void>((resolve) => {
    try {
      player.ready(resolve)
    } catch {
      resolve()
    }
  })
}

export const configurePlayerOptions = (
  player: any,
  options: { muted?: boolean; autoplay?: boolean }
): void => {
  if (options.muted) player.muted(true)
  if (options.autoplay) player.autoplay(true)
}

export const ensurePlayerSize = (element: HTMLElement | null, player: any): void => {
  if (!element || !player) return

  const rect = element.getBoundingClientRect()
  if (rect.width > 0 && rect.height > 0) {
    try {
      player.fluid(false)
      player.width(Math.round(rect.width))
      player.height(Math.round(rect.height))
    } catch {}
  }
}

export const updateVideoElementSize = (
  element: HTMLElement | null,
  videoWidth: string
): void => {
  if (!element) return

  element.style.width = videoWidth
  element.style.height = `${FULL_VIDEO_VIEW_HEIGHT}vh`
}

export const loadVideoInPlayer = (player: any, videoId: string): void => {
  try {
    player.catalog?.getVideo?.(videoId, (err: any, video: any) => {
      if (err) {
        console.error('Error loading video', err)
        return
      }
      if (video) {
        player.catalog.load(video)
      } else {
        console.warn('No video found for', videoId)
      }
    })
  } catch (error) {
    console.error('loadVideoInPlayer: exception', error)
  }
}

export const cleanupPlayer = (player: any): void => {
  try {
    player?.dispose?.()
  } catch {}
}

export const generatePlayerId = (): string => {
  return `bc-player-${Math.random().toString(36).slice(2)}`
}
